import React from 'react'
import ColumnGridPanel from 'components/ColumnGridPanel'

import FurnaceReport from 'pages/Device/SystemStatusPanel/FurnaceReport'
import {
  defaultIfOutrageous,
  friendlyName,
  onOff,
  cubicFeetPerMinute,
  degreesFahrenheit,
  staticPressure,
  percentage,
  precision,
  rpm,
  voltageAC
} from 'helpers/formatters'
import { FRIENDLY_NAMES } from 'helpers/reportFriendlyNames'
import { IndoorReport } from 'types/SystemStatusReport'
import _ from 'underscore'

type Props = {
  report: IndoorReport
}

const IndoorUnit = ({ report }: Props) => {
  const anyFurnace = report.hasS9ModelFurnace || report.hasS8ModelFurnace

  const indoorReport = {
    extendedIndoorMotorAirFlow: cubicFeetPerMinute(report.extendedIndoorMotorAirFlow),
    extendedIndoorTargetAirflow: anyFurnace ? cubicFeetPerMinute(report.extendedIndoorTargetAirflow) : null,
    extendedIndoorStaticPressure: staticPressure(report.extendedIndoorMotorStaticPressure),
    extendedIndoorInducerSpeedActual: rpm(report.extendedIndoorInducerSpeedActual),
    extendedIndoorCombustionPressureActual: staticPressure(report.extendedIndoorCombustionPressureActual),
    extendedIndoorCombustionPressureTarget: staticPressure(report.extendedIndoorCombustionPressureTarget),
    extendedIndoorGasValvePositionActual: percentage(report.extendedIndoorGasValvePositionActual, 1),
    extendedIndoorGasValvePositionSetpoint: percentage(report.extendedIndoorGasValvePositionSetpoint, 1),
    extendedIndoorTemperatureRise: degreesFahrenheit(report.extendedIndoorTemperatureRise),
    extendedIndoorTemperatureRiseTarget: degreesFahrenheit(report.extendedIndoorTemperatureRiseTarget),
    extendedIndoorLineVoltage: voltageAC(report.extendedIndoorLineVoltage),
    requestedModulationCapacity: percentage(report.requestedModulationCapacity, 1),
    extendedIndoorMotorStaticPressure: staticPressure(report.extendedIndoorMotorStaticPressure),
    extendedIndoorCoilTemp: !anyFurnace ? degreesFahrenheit(report.extendedIndoorCoilTemp) : null,
    extendedIndoorGasTemp: !anyFurnace ? degreesFahrenheit(report.extendedIndoorGasTemp) : null,
    extendedIndoorGasSuperheat: (!anyFurnace && report.operatingStatus === 'SYSTEM_COOLING') ? degreesFahrenheit(report.extendedIndoorGasSuperheat) : null,
    extendedIndoorEevStepPosition: (!anyFurnace && report.deviceType === 'control_hub') ? precision(report.extendedIndoorEevStepPosition, 0) : null,
    dischargeAirTemperature: (report.deviceType === 'control_hub' && report.hasIndoorSupplyAirTempSensorEnabled) ? degreesFahrenheit(report.dischargeAirTemperature) : null,
    returnAirTemperature: (report.deviceType === 'control_hub' && report.hasIndoorReturnAirTempSensorEnabled) ? degreesFahrenheit(report.returnAirTemperature) : null,
    extendedIndoorSupplyStaticPressure: staticPressure(report.extendedIndoorSupplyStaticPressure),
    extendedIndoorReturnStaticPressure: report.hasIndoorReturnStaticSensorEnabled ? staticPressure(report.extendedIndoorReturnStaticPressure) : null,
    relativeHumidity: (!_.isEmpty(report.zones) && report.zones.length > 1) ? percentage(report.relativeHumidity, 1) : null
  }

  const indoorFurnaceReport = {
    extendedIndoorFurnaceDemandStatus: friendlyName(report.extendedIndoorFurnaceDemandStatus, FRIENDLY_NAMES),
    extendedIndoorFurnaceOperationMode: friendlyName(report.extendedIndoorFurnaceOperationMode, FRIENDLY_NAMES),
    extendedIndoorFurnaceInducerErrorHandling: defaultIfOutrageous(report.extendedIndoorFurnaceInducerErrorHandling),
    extendedIndoorFurnaceUnitOrientation: friendlyName(report.extendedIndoorFurnaceUnitOrientation, FRIENDLY_NAMES),
    extendedIndoorFurnaceInducerStage: friendlyName(report.extendedIndoorFurnaceInducerStage, FRIENDLY_NAMES),
    extendedIndoorFurnaceGasValveStatus: friendlyName(report.extendedIndoorFurnaceGasValveStatus, FRIENDLY_NAMES),
    pressureSwitchOne: onOff(report.extendedIndoorFurnaceSwitchStatus?.pressureSwitchOne),
    pressureSwitchTwo: report.hasS8ModelFurnace ? onOff(report.extendedIndoorFurnaceSwitchStatus?.pressureSwitchTwo) : null,
    highLimitStatus: onOff(report.extendedIndoorFurnaceSwitchStatus?.highLimitStatus),
    inducerLimitStatus: onOff(report.extendedIndoorFurnaceSwitchStatus?.inducerLimitStatus),
    condensateStatus: onOff(report.extendedIndoorFurnaceSwitchStatus?.condensateStatus),
    ignitorStatus: onOff(report.extendedIndoorFurnaceSwitchStatus?.ignitorStatus),
    rolloutLimitInputStatus: onOff(report.extendedIndoorFurnaceSwitchStatus?.rolloutLimitInputStatus),
    reverseAirflowStatus: onOff(report.extendedIndoorFurnaceSwitchStatus?.reverseAirflowStatus),
    flameStatus: onOff(report.extendedIndoorFurnaceSwitchStatus?.flameStatus),
    accessoryOneStatus: onOff(report.extendedIndoorFurnaceSwitchStatus?.accessoryOneStatus),
    accessoryTwoStatus: onOff(report.extendedIndoorFurnaceSwitchStatus?.accessoryTwoStatus)
  }

  const hasData = Object.values(indoorReport).some(val => val !== null)
  const hasFurnaceData = anyFurnace && Object.values(indoorFurnaceReport).some(val => val !== null)

  if (!hasData && !hasFurnaceData) {
    return null
  }

  return (
    <ColumnGridPanel.Column>
      <ColumnGridPanel.Column.Header>Indoor Unit</ColumnGridPanel.Column.Header>
      <ColumnGridPanel.Column.Item label="Actual Airflow" value={indoorReport.extendedIndoorMotorAirFlow} hideIfValueIsNull />

      <ColumnGridPanel.Column.Item label="Target Airflow" value={indoorReport.extendedIndoorTargetAirflow} hideIfValueIsNull />

      <ColumnGridPanel.Column.Item label="Total Static Pressure" value={indoorReport.extendedIndoorMotorStaticPressure} hideIfValueIsNull />

      <ColumnGridPanel.Column.Item label="Actual Inducer Speed" value={indoorReport.extendedIndoorInducerSpeedActual} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Actual Combustion Pressure" value={indoorReport.extendedIndoorCombustionPressureActual} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Target Combustion Pressure" value={indoorReport.extendedIndoorCombustionPressureTarget} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Actual Gas Valve Stepper Position" value={indoorReport.extendedIndoorGasValvePositionActual} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Gas Valve Setpoint" value={indoorReport.extendedIndoorGasValvePositionSetpoint} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Temperature Rise - Measured" value={indoorReport.extendedIndoorTemperatureRise} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Target Temperature Rise" value={indoorReport.extendedIndoorTemperatureRiseTarget} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Line Voltage" value={indoorReport.extendedIndoorLineVoltage} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Target Demand" value={indoorReport.requestedModulationCapacity} hideIfValueIsNull />

      <ColumnGridPanel.Column.Item label="Evap/Liquid Temp" value={indoorReport.extendedIndoorCoilTemp} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="Gas Temp" value={indoorReport.extendedIndoorGasTemp} hideIfValueIsNull />

      <ColumnGridPanel.Column.Item label="ID Superheat" value={indoorReport.extendedIndoorGasSuperheat} hideIfValueIsNull />

      <ColumnGridPanel.Column.Item label="ID EEV Steps" value={indoorReport.extendedIndoorEevStepPosition} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="ID Supply Air Temp" value={indoorReport.dischargeAirTemperature} hideIfValueIsNull />
      <ColumnGridPanel.Column.Item label="ID Return Air Temp" value={indoorReport.returnAirTemperature} hideIfValueIsNull />

      <ColumnGridPanel.Column.Item label="Supply Static Pressure" value={indoorReport.extendedIndoorSupplyStaticPressure} hideIfValueIsNull />

      <ColumnGridPanel.Column.Item label="Return Static Pressure" value={indoorReport.extendedIndoorReturnStaticPressure} hideIfValueIsNull />

      {anyFurnace && <FurnaceReport report={indoorFurnaceReport} />}

      <ColumnGridPanel.Column.Item label="Indoor Rh" value={indoorReport.relativeHumidity} hideIfValueIsNull />
    </ColumnGridPanel.Column>
  )
}

export default IndoorUnit
